import { lazy, Suspense, useMemo, useContext } from "react"
import { Loader } from "widgets"
import RouteError from "../base/route-error.js"
import PrivateRoute from "../base/private-route.js"
import { featuresFlatMap } from "utils"
import { AppContext } from "../base/app-context-provider.js"

const ManageSecurityGroup = lazy(
  () => import("../security-groups/manage-security-group.js"),
)
const SecurityGroup = lazy(() => import("../security-groups/security-group.js"))

const ManageUser = lazy(() => import("../user/manage-user.js"))
const UserDetails = lazy(() => import("../user/user-details.js"))
const CreateUser = lazy(() => import("../user/create-user.js"))
const ChangePassword = lazy(() => import("../user/change-password.js"))

export const useUserRoutes = () => {
  const appContext = useContext(AppContext)

  const features = useMemo(
    () => featuresFlatMap(appContext?.user?.featureDTOList)?.["users"] ?? [],
    [appContext?.user?.featureDTOList],
  )

  const userRoutes = useMemo(
    () =>
      _.compact([
        {
          path: "user",
          errorElement: <RouteError />,
          element: <PrivateRoute />,
          children: _.compact([
            features["search-user"] && {
              path: "manage-user",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageUser />
                </Suspense>
              ),
            },
            features["modify-user"] && {
              path: "modify-user",
              element: (
                <Suspense fallback={<Loader />}>
                  <UserDetails
                    mode="modify"
                    parentRoute="/user/manage-user"
                    title="Modify User"
                  />
                </Suspense>
              ),
            },
            features["create-user"] && {
              path: "create-user",
              element: (
                <Suspense fallback={<Loader />}>
                  <CreateUser
                    parentRoute="/user/manage-user"
                    title="Create User"
                  />
                </Suspense>
              ),
            },
            features["create-agency-user"] &&
             {
              path: "create-agency-user",
              element: (
                <Suspense fallback={<Loader />}>
                  <CreateUser
                    parentRoute="/user/manage-user"
                    title="Create Agency User"
                    type="agency"
                  />
                </Suspense>
              ),
            },
            features["view-user"] && {
              path: "view-user",
              element: (
                <Suspense fallback={<Loader />}>
                  <UserDetails
                    mode="view"
                    parentRoute="/user/manage-user"
                    title="View User"
                  />
                </Suspense>
              ),
            },
            features["manage-profile"] && {
              path: "manage-profile",
              element: (
                <Suspense fallback={<Loader />}>
                  <UserDetails
                    mode="manage-profile"
                    parentRoute="/"
                    title="Manage User Profile"
                  />
                </Suspense>
              ),
            },
            features["change-password"] && {
              path: "change-password",
              element: (
                <Suspense fallback={<Loader />}>
                  <ChangePassword />
                </Suspense>
              ),
            },
            features["search-security-group"] && {
              path: "manage-security-group",
              element: (
                <Suspense fallback={<Loader />}>
                  <ManageSecurityGroup />
                </Suspense>
              ),
            },
            features["modify-security-group"] && {
              path: "modify-security-group",
              element: (
                <Suspense fallback={<Loader />}>
                  <SecurityGroup
                    mode="modify"
                    parentRoute="/user/manage-security-group"
                    title="Modify Security Group"
                  />
                </Suspense>
              ),
            },
            features["view-security-group"] && {
              path: "view-security-group",
              element: (
                <Suspense fallback={<Loader />}>
                  <SecurityGroup
                    mode="view"
                    parentRoute="/user/manage-security-group"
                    title="View Security Group"
                  />
                </Suspense>
              ),
            },
            features["create-security-group"] && {
              path: "create-security-group",
              element: (
                <Suspense fallback={<Loader />}>
                  <SecurityGroup
                    mode="create"
                    parentRoute="/user/manage-security-group"
                    title="Create Security Group"
                  />
                </Suspense>
              ),
            },
          ]),
        },
      ]),
    [features],
  )

  return userRoutes
}
